/**
 * A porta do painel.
 *
 * É a única tela da prancheta sem lateral: a lateral lê sessão, e aqui ainda
 * não há uma. Montar a casca antes de haver quem a ocupe é como um menu acaba
 * piscando um papel que a pessoa não tem.
 *
 * O convidado nunca chega aqui. Ele não tem conta, e o link dele (`#/f/…`) não
 * passa por sessão nenhuma.
 */

import { useState } from 'react';

import { entrar as entrarNaApi } from '../../servicos/api';
import { mensagemDoErro } from '../../servicos/gestao';
import { AvisoDeErro } from '../../componentes/gestao/pecas';
import { enderecoDaRota, irPara } from '../../rotas';
import '../../estilo/gestao.css';

export type PropsDeEntrar = {
  readonly aoEntrar: (email: string, senha: string) => void;
  readonly erro: string | null;
  readonly enviando: boolean;
};

export function Entrar({ aoEntrar, erro, enviando }: PropsDeEntrar) {
  const [email, definirEmail] = useState('');
  const [senha, definirSenha] = useState('');

  return (
    <main className="entrar">
      <form
        className="entrar__coluna"
        onSubmit={(e) => {
          e.preventDefault();
          if (!enviando) aoEntrar(email.trim(), senha);
        }}
      >
        <div>
          <h1>Entrar no painel</h1>
          <p className="entrar__apoio">
            Este é o painel de quem organiza. Quem foi convidado não entra aqui — o link do
            formulário abre direto, sem conta.
          </p>
        </div>

        {erro ? <AvisoDeErro mensagem={erro} /> : null}

        <label className="campo">
          <span className="campo__rotulo">E-mail</span>
          <input
            className="campo__controle"
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => definirEmail(e.target.value)}
            aria-invalid={erro ? true : undefined}
          />
        </label>

        <label className="campo">
          <span className="campo__rotulo">Senha</span>
          <input
            className="campo__controle"
            type="password"
            name="senha"
            autoComplete="current-password"
            required
            value={senha}
            onChange={(e) => definirSenha(e.target.value)}
            aria-invalid={erro ? true : undefined}
          />
        </label>

        <button
          type="submit"
          className="botao botao--primario"
          disabled={enviando}
          aria-busy={enviando ? 'true' : undefined}
        >
          {enviando ? 'Entrando' : 'Entrar'}
        </button>
      </form>
    </main>
  );
}

/** A versão que fala com o banco. É esta que `App.tsx` monta. */
export function TelaEntrar() {
  const [erro, definirErro] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  const aoEntrar = (email: string, senha: string) => {
    definirEnviando(true);
    definirErro(null);
    entrarNaApi(email, senha)
      .then(() => {
        // A fila é o lugar de quem acabou de entrar: é o que ela mais olha.
        irPara({ nome: 'fila' });
      })
      .catch((e: unknown) => definirErro(mensagemDoErro(e, 'entrar')))
      .finally(() => definirEnviando(false));
  };

  return <Entrar aoEntrar={aoEntrar} erro={erro} enviando={enviando} />;
}

/**
 * O que a prancheta mostra a quem não entrou.
 *
 * Não redireciona sozinha: um `irPara` dentro de render dispara antes de a
 * sessão terminar de carregar e joga para fora quem só estava esperando o
 * token voltar do armazenamento.
 */
export function PrecisaEntrar() {
  return (
    <div className="vazio">
      <span className="vazio__titulo">Você não está logada</span>
      <p>O painel é de quem organiza. Entre para ver os formulários desta organização.</p>
      <a className="botao botao--primario" href={enderecoDaRota({ nome: 'entrar' })}>
        Entrar
      </a>
    </div>
  );
}
